<template>
  <div id="root" ref="root">
    <Loader v-if="!initialized" />

    <div class="filters" @click="hideShownFilter()">
      <!-- Search filter -->
      <form class="form-inline" @submit.prevent="addSearchTerm()">
        <label for="search-filter" class="font-weight-bold">
          {{ appTranslate('filter on') }}
          {{ ['item no', 'name', 'barcode', 'recurrenceId'].map((str) => translate(str, 'attributes')).join(' / ') }}
        </label>

        <div class="w-100">
          <label class="d-inline-block">
            <input v-model="matchExactSearch" type="checkbox" class="m-1" />
            {{ appTranslate('Exact match') }}
          </label>
        </div>

        <div class="w-100">
          <label class="d-inline-block">
            <input v-model="showAddMultipleSearchTerms" type="checkbox" class="m-1" />
            {{ appTranslate('Add multiple at once') }}
          </label>
        </div>

        <textarea
          v-show="showAddMultipleSearchTerms"
          ref="searchTextarea"
          v-model="stagedSearchValue"
          rows="10"
          class="w-100 form-control"
          :placeholder="appTranslate('Add multiple search terms separated by line break or comma')"
        />
        <input
          v-show="!showAddMultipleSearchTerms"
          id="search-filter"
          v-model="stagedSearchValue"
          type="search"
          class="form-control"
          :placeholder="appTranslate('Type something')"
        />
        <button type="submit" class="btn btn-primary m-2">
          {{ appTranslate('Add') }} {{ showAddMultipleSearchTerms ? appTranslate('all', undefined, 'lowercase') : '' }}
        </button>

        <!-- Active search terms -->
        <template v-if="activeSearchValues.length">
          <div class="w-100 d-flex">
            {{ appTranslate('Active search terms') }}
            <button class="link clear" title="Rensa alla söktermer" @click="removeSelectedFiltersIn(searchKey)">&times;</button>
          </div>
          <ul class="active-search-terms minimalistic">
            <li v-for="(searchTerm, i) of activeSearchValues" :key="i" class="link" @click="removeSearchTerm(searchTerm)">
              &times;
              <span class="ml-2">
                {{ searchTerm }}
              </span>
              <!-- <span class="clear"> &times; </span> -->
            </li>
          </ul>
        </template>
      </form>

      <!-- Categories -->
      <div v-if="initialized" class="d-flex position-relative">
        <!-- <InfoPopup no-shadow content-style="left: 0; transform: translateX(10%)" :label="translate('categories', 'taxonomies', 'capitalize')"> -->
        <InfoPopup no-shadow content-style="left: 0; transform: translateX(-10px); max-width:100%">
          <template #label>{{ translate('categories', 'taxonomies', 'capitalize') }}</template>
          <h5>{{ appTranslate('Legend') }}</h5>
          <span class="checkbox checked" />
          {{ appTranslate('Category explicitly chosen') }}. {{ appTranslate('included') }}
          <br />
          <span class="checkbox selected-parent" />
          {{ appTranslate('Category included because a supercategory has been selected') }}
          <br />
          <span class="checkbox selected-child" />
          <span
            v-html="
              `${appTranslate('Category not included').replace(/\s(not|inte|ikke|ei)\s/i, ' <strong>$1</strong> ')}. ${appTranslate(
                'One or more subcategories are selected',
              )}`
            "
          />
        </InfoPopup>

        <button
          v-if="hasActiveCategoryFilters"
          class="link clear"
          :title="appTranslate('Clear category filter')"
          @click="removeSelectedFiltersIn('_categories')"
        >
          &times;
        </button>
      </div>

      <ToggledMenu :refreshing="refreshing" :categories="categories" @input="handleUpdateFilter('_categories', $event.key, $event.value)" />

      <!-- Attribute & Taxonomy filters -->
      <div v-for="(filterGroup, groupIndex) of filterGroups" :key="groupIndex" class="filter-group">
        <!-- Individual filter -->
        <div v-for="(filter, filterIndex) of filterGroup" :key="filterIndex" class="d-flex position-relative">
          <div class="filter">
            <!-- :disabled="!filter.options.length" -->
            <button
              :ref="filter.id"
              class="select"
              :class="{ 'has-options-selected': hasSelectedOptions(filter), focused: filterIsShown(filter), disabled: filterShouldShowAsDisabled(filter) }"
              style="cursor: default"
              :title="`${filter.name} (${filter.options.length})`"
              @click.stop="toggleShownFilter(filter)"
            >
              <span class="filter-name" :class="{ 'no-terms': !filter.hasTerms && filter.taxonomy === 'attributes' }">
                {{ filter.name }}
                <i v-if="isRefreshing(filter)" class="spinner spinner-border spinner-border-sm refresh-spinner" />
                <template v-else> ({{ filter.options.length }}) </template>
              </span>
              <i class="fa fa-angle-down expand" />
            </button>

            <div v-if="filterIsShown(filter)" class="list" @click.stop>
              <!-- Filter options search -->
              <div class="m-1">
                <input
                  v-if="filter.options.length > 3"
                  :ref="`search-${filter.id}`"
                  v-model="filterSearch[filter.id]"
                  type="search"
                  class="w-100 filter-search"
                  placeholder="Sök..."
                  @keydown.stop="handleEscape($event, filter)"
                />
              </div>

              <div :ref="`list-${filter.id}`" class="options minimalistic">
                <div class="mb-2 override-toggles">
                  <!-- Toggle all -->
                  <label
                    v-if="filter.options.length > 1 && (filter.hasTerms || filter.taxonomy !== 'attributes') && getFilteredOptions(filter)?.length"
                    class="pl-2 link"
                    style="width: unset"
                  >
                    <input type="checkbox" class="toggle-all d-none" @input="toggleAll(filter, !areAllSelected(filter))" />
                    {{ areAllSelected(filter) ? appTranslate('Select none') : appTranslate('Select all') }}
                  </label>

                  <!-- Any -->
                  <label v-if="filter.taxonomy === 'attributes' && !filter.hasTerms" class="pl-2 link">
                    <input
                      type="checkbox"
                      :checked="isSelected(filter, WildcardKey)"
                      @click="handleUpdateFilter(filter[FilterKey], WildcardKey, !isSelected(filter, WildcardKey))"
                    />
                    {{ appTranslate('Match any') }}
                  </label>
                </div>
                <!-- Filter options -->
                <label
                  v-for="(option, optionIndex) of getFilteredOptions(filter)"
                  :key="optionIndex"
                  class="option"
                  :class="{
                    'line-through': isSelected(filter, NoneKey) && !isSelected(filter, option.id),
                    none: option.id === NoneKey,
                  }"
                >
                  <input
                    type="checkbox"
                    class="mr-1"
                    :class="{ disabled: isSelected(filter, WildcardKey) }"
                    :disabled="isRefreshing(filter) || isOverridden(filter) || isSelected(filter, WildcardKey)"
                    :checked="isSelected(filter, option[FilterKey]) || (isSelected(filter, WildcardKey) && option.id !== NoneKey)"
                    @click="handleUpdateFilter(filter[FilterKey], option[FilterKey], ($event.target as HTMLInputElement).checked, $event)"
                    @keydown.stop="handleEscape($event, filter, true)"
                  />
                  {{ option.name }}
                  <i v-if="isRefreshing(filter)" class="spinner spinner-border spinner-border-sm refresh-spinner" />
                  <template v-else-if="option.matches !== undefined"> ({{ option.matches }}) </template>
                </label>
                <em v-if="!getFilteredOptions(filter)?.length" class="option"> {{ appTranslate('No hits') }} </em>
              </div>
            </div>
          </div>
          <button class="link clear" :class="{ hidden: !hasSelectedOptions(filter) }" @click="removeSelectedFiltersIn(filter[FilterKey] as string)">
            &times;
          </button>
        </div>
      </div>

      <div class="d-flex flex-wrap">
        <!-- Advanced filters -->

        <div v-for="(_, i) of customFilters" :key="i" class="custom-filter w-100">
          <CustomFilterComponent
            v-model="customFilters[i]"
            @change="
              handleSetFilter(
                getCustomFilterKey($event.filter),
                $event.filter.options,
                $event.previousFilter ? getCustomFilterKey($event.previousFilter) : undefined,
              )
            "
          />
          <button class="link clear" style="height: 1em" @click="removeCustomFilter(i)">&times;</button>
        </div>

        <button class="btn btn-light mb-2 w-100 mr-4" style="border: 1px solid var(--light-grey)" @click="addCustomFilter()">
          {{ appTranslate('Add advanced filter') }} <i clasS="fa fa-flask" />
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import ToggledMenu from '@/components/category-menu/ToggledMenuComponent.vue'
import CustomFilterComponent from '@/components/products/filter/CustomFilterComponent.vue'
import InfoPopup from '@/components/simple/InfoPopupComponent.vue'
import Loader from '@/components/simple/LoaderComponent.vue'
import { useFilter } from '@/composables/useFilter'
import { useTools } from '@/composables/useTools'
import { useTranslation } from '@/composables/useTranslation'
import { CustomFilter, Filter, FilterKey, FilterOperator, NoneKey, WildcardKey } from '@/models/Filter'
import { StringObject } from '@/models/GenericInterfaces'
import { SimplifiedCategory, SimplifiedTaxonomicObject } from '@/models/Taxonomy'
import DataService from '@/services/DataService'
import PropertyService from '@/services/PropertyService'
import { useDefaultStore } from '@/store'
import { browserStorage } from '@/utils/browserStorage'
import { Debouncer } from '@/utils/Debouncer'
import { sortCategories } from '@/utils/productUtils'
import { clone, splitSearchTerm, toComparable, arrayToMap } from '@/utils/tools'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// -----------
// -- Setup --
// -----------
const props = withDefaults(
  defineProps<{
    softLimit?: number
    bodyHeight?: string
    activated?: boolean
  }>(),
  { softLimit: 0, bodyHeight: 'auto' },
)

const store = useDefaultStore()
const { setFilter, updateFilter, toggleAllFilters } = useFilter()
const { translate, appTranslate } = useTranslation()
const route = useRoute()
const router = useRouter()
const { getRef } = useTools()

const searchKey = '_q'
let filtersTimeout = 0
let refreshFiltersDelay = 0

const saveSettingsDebouncer = new Debouncer()
const textareaResizeObserver = new ResizeObserver((entries) => {
  saveSettingsDebouncer.debounce(() => {
    const height = entries[0]?.contentRect.height

    if (height) {
      browserStorage.set('filter-searchTextareaHeight', `${height}px`)
    }
  }, 500)
})

// --------------
// -- Reactive --
// --------------
const shownFilter = ref<string | undefined>()
const filterGroups = ref<Array<Filter[]>>()
const stagedSearchValue = ref('')
const categories = ref<SimplifiedCategory[]>()
const initialized = ref(false)
const refreshing = ref(false)
const latestQuery = ref('')
const showAddMultipleSearchTerms = ref(browserStorage.get('productsFilter-addMultipleSearchTerms'))
const lastAddedFilter = ref('') // used to determine whether to show loading symbols for the filter (matching count will never change for filters in the group in which the new filter was applied)
const filterSearch = ref<StringObject>({})
const customFilters = ref<CustomFilter[]>([])

const root = ref<HTMLElement>()
const searchTextarea = ref<HTMLTextAreaElement>()

// --------------
// -- Computed --
// --------------
const activeSearchValues = computed(() =>
  Object.entries(route.query)
    .filter(([key]) => key?.startsWith(searchKey))
    .flatMap(([, value]) => value?.toString().split(',') || []),
)

const hasActiveCategoryFilters = computed(() => Boolean(route.query['_categories']))

const matchExactSearch = computed({
  get: () => route.query.exactSearch == '1',
  set(value: boolean) {
    const query = { ...route.query }
    if (value) {
      query.exactSearch = '1'
    } else {
      delete query.exactSearch
    }
    router.push({ query })
  },
})

// --------------
// -- Watchers --
// --------------
watch(
  () => props.activated,
  (isActive) => {
    if (isActive && latestQuery.value !== route.fullPath) {
      getFilters().then(() => updateCustomFiltersFromQuery())
    } else if (!isActive) {
      shownFilter.value = undefined
    }
  },
)

watch(
  route,
  (route) => {
    if (props.activated && route.fullPath !== latestQuery.value) {
      clearTimeout(filtersTimeout)
      filtersTimeout = window.setTimeout(() => {
        getFilters()
        refreshFiltersDelay = 0
      }, refreshFiltersDelay)
    }

    updateCustomFiltersFromQuery()
  },
  { deep: true },
)

watch(
  () => showAddMultipleSearchTerms.value,
  (value) => {
    browserStorage.set('productsFilter-addMultipleSearchTerms', value)
  },
)

// ---------------
// -- Lifecycle --
// ---------------
onMounted(() => {
  if (props.activated && latestQuery.value !== route.fullPath) {
    getFilters().then(() => updateCustomFiltersFromQuery())
  }

  if (searchTextarea.value) {
    textareaResizeObserver.observe(searchTextarea.value)
    const savedHeight = browserStorage.get<string>('filter-searchTextareaHeight')

    if (savedHeight) {
      searchTextarea.value.style.height = savedHeight
    }
  }
})

// ---------------
// -- Functions --
// ---------------
const updateCustomFiltersFromQuery = () => {
  // TODO CustomFiltersComponent's responsibility?
  const filterToComparable = (filter: CustomFilter) => filter.fieldPath.join('.')
  const defaultFilters = new Set(filterGroups.value?.flat().map((filter) => filter.id))

  const customFiltersFromQuery: CustomFilter[] = Object.entries(route.query)
    .filter(([key]) => key.startsWith('_field') && !defaultFilters.has(key))
    .map(([key, value]) => {
      const data = key.split('_')
      const fieldPath = data[2]?.split('.') || []
      const operator: FilterOperator = (data[3] as FilterOperator) || 'equals'

      return {
        fieldPath,
        options: value?.toString()?.split(',') || [],
        operator,
        active: true,
      }
    })

  const indexedCustomFiltersFromQuery = customFiltersFromQuery.reduce(
    (acc, filter) => {
      acc[filterToComparable(filter)] = filter
      return acc
    },
    {} as { [comparable: string]: CustomFilter },
  )

  // update existing custom filters from query
  const existingCustomFiltersUpdatedFromQuery = customFilters.value.map((filter) => {
    const foundFilter = indexedCustomFiltersFromQuery[filterToComparable(filter)]
    if (foundFilter) {
      return foundFilter
    } else {
      return {
        ...filter,
        active: false,
      }
    }
  })

  const existingCustomFiltersComparable = new Set(customFilters.value.map((filter) => filterToComparable(filter)))
  // add custom filters from query if they don't already exist
  const nonExistingCustomFiltersFromQuery = customFiltersFromQuery.filter((filter) => !existingCustomFiltersComparable.has(filterToComparable(filter)))

  customFilters.value = [...existingCustomFiltersUpdatedFromQuery, ...nonExistingCustomFiltersFromQuery]
}

const getFilteredOptions = (filter: Filter & { search?: string }) => {
  return filterSearch.value[filter.id]
    ? filter.options.filter((option) => toComparable(option.name).includes(toComparable(filterSearch.value[filter.id])))
    : filter.options
}

const getFilters = async () => {
  const queryHasChanged = latestQuery.value !== DataService.getFilterMatchUrl(route.query)
  if (queryHasChanged) {
    refreshing.value = true

    const filtersResponse = await DataService.getFiltersWithMatches(route.query)
    const propertyGroups = await PropertyService.getPropertyGroups('filter', store.dataCulture)
    const filtersByComparable = arrayToMap(filtersResponse?.filters || [], 'defaultName', toComparable)

    if (filtersResponse) {
      const _filterGroups = propertyGroups.map((group) => {
        return group.flatMap((property) => filtersByComparable.get(toComparable(property.defaultName)) || [])
      })

      const queryIsLatest = filtersResponse.query === DataService.getFilterMatchUrl(route.query)
      if (queryIsLatest) {
        latestQuery.value = filtersResponse.query

        const foundCategoryOptions = filtersResponse?.filters.find((filter) => toComparable(filter.defaultName) === 'categories')
          ?.options as SimplifiedCategory[]
        if (foundCategoryOptions) {
          categories.value = sortCategories(foundCategoryOptions, undefined, 'asc', 'desc')
        }

        filterGroups.value = _filterGroups.map((filters) => {
          return filters.filter((filter) => {
            if (filter.options?.length) {
              filter.options = filter.options.filter((option) => option.matches || isSelected(filter, option[FilterKey]))
              return true
            }
          })
        })
      }
    }
    refreshing.value = false
    initialized.value = true
  }
}

const openFilterDropdown = async (filter?: Filter) => {
  shownFilter.value = filter?.id
  if (filter) {
    // auto scroll into view and focus input
    await nextTick()
    const searchInput = getRef<HTMLElement | null>(`search-${filter.id}`)
    const optionsList = getRef<HTMLElement | null>(`list-${filter.id}`)

    if (optionsList) {
      const listRect = optionsList?.getBoundingClientRect()
      const scrollMargin = 100
      const parent = root.value?.parentElement

      if (parent) {
        const scrollBy = listRect.bottom + scrollMargin - window.innerHeight
        if (scrollBy > 0) {
          setTimeout(() => parent.scrollBy({ top: listRect.bottom - window.innerHeight + scrollMargin, behavior: 'smooth' }))
        }

        setTimeout(() => searchInput?.focus(), scrollBy > 0 ? 300 : 20) // focus input after potential scroll (will mess up scroll if focused too early)
      }
    }
  }
}

const hideShownFilter = () => openFilterDropdown()

const filterIsShown = (filter?: Filter) => shownFilter.value === filter?.id

const toggleShownFilter = (filter: Filter) => {
  if (filterIsShown(filter)) {
    openFilterDropdown()
  } else {
    openFilterDropdown(filter)
  }
}

const hasSelectedOptions = (filter: Filter) => !!route.query[filter.id]

const isSelected = (filter: Filter, value: string, negated?: boolean) => {
  value = value?.replace(/,/g, '.')
  const val = negated ? `!${value}` : value
  const values = route.query[filter[FilterKey]]
  return values?.toString().split(',').includes(val)
}

const areAllSelected = (filter: Filter) => {
  const filters = route.query[filter[FilterKey]]
  const values = new Set(Array.isArray(filters) ? filters : filters?.split(','))
  return getFilteredOptions(filter).every((option) => values.has(option.id) || option.id === NoneKey)
}

const removeSelectedFiltersIn = (filterKey: string) => {
  hideShownFilter()
  handleSetFilter(filterKey)
}

const handleSetFilter = async (key: string, values?: string[] | undefined, previousFilterKey?: string) => {
  lastAddedFilter.value = key
  await setFilter(key, values, previousFilterKey)
}

// // unlike setFilter, adds or removes value from query key (depending on state of enable) and navigates.
const handleUpdateFilter = (key: string, value: string, enable: boolean, event?: Event): Promise<boolean> => {
  refreshFiltersDelay = 1000
  lastAddedFilter.value = key
  return updateFilter(key, value, enable, event)
}

const toggleAll = async (filter: Filter, toggleOn: boolean) => {
  lastAddedFilter.value = filter[FilterKey]
  refreshFiltersDelay = 1500
  await toggleAllFilters(filter[FilterKey], getFilteredOptions(filter), toggleOn)
}

const addSearchTerm = async () => {
  let successful = false
  if (stagedSearchValue.value?.trim()) {
    if (showAddMultipleSearchTerms.value) {
      const terms = splitSearchTerm(stagedSearchValue.value)

      const query = clone(route.query)
      const prevTerms = query[searchKey] ? String(query[searchKey])?.split(',') : []
      query[searchKey] = prevTerms.concat(terms).join(',')

      successful = !(await router.push({ query }))
    } else {
      successful = await handleUpdateFilter(searchKey, stagedSearchValue.value, true)
    }
  }

  if (successful) {
    stagedSearchValue.value = ''
  }
}

const removeSearchTerm = (term: string) => {
  handleUpdateFilter(searchKey, term, false)
  stagedSearchValue.value = ''
}

const isRefreshing = (filter: Filter) => refreshing.value && lastAddedFilter.value !== filter.id

const isOverridden = (filter: Filter) => filter[FilterKey] === WildcardKey || filter[FilterKey] === NoneKey

const handleEscape = (event: KeyboardEvent, filter: Filter, alwaysClose = false) => {
  if (event.key === 'Escape' && (!filterSearch.value[filter.id] || alwaysClose)) {
    hideShownFilter()
  }
}

const addCustomFilter = () => {
  customFilters.value.push({ fieldPath: [], operator: 'range', active: false, options: [] })
}

const getCustomFilterKey = (filter: CustomFilter) => `_field_${filter.fieldPath.join('.')}_${filter.operator}`

const removeCustomFilter = (i: number) => {
  const filter = customFilters.value[i]
  const filterKey = getCustomFilterKey(filter)
  handleSetFilter(filterKey)
  lastAddedFilter.value = filterKey
  customFilters.value.splice(i, 1)
}

const filterShouldShowAsDisabled = (filter: Filter) => !filter.options.some((opt) => opt.id !== NoneKey)
</script>

<style lang="scss" scoped>
.overlay {
  z-index: 0;
}

label {
  user-select: none;
}

.filters {
  // -- Experimental to allow easier closing of dropdowns. Effectively removes padding/margin from modal, and adds padding to filter instead -
  margin: -0.5em -1em -0.5em -1em;
  padding: 0.5em 0.5em 0.5em 1em;

  @media (min-width: 576px) {
    margin-left: -2em;
    margin-right: -2em;
    padding-left: 2em;
    padding-right: 1em;
  }
  // ----------------

  // margin-top: 1em;

  > * {
    margin-bottom: 1.5em;
  }

  #search-filter {
    flex-grow: 1;
  }

  .clear {
    margin-left: auto;
    font-size: 1.4em;
    line-height: 1rem;
  }

  .filter-group {
    break-inside: avoid;

    .filter {
      display: flex;
      width: 100%;
      max-width: calc(100% - 25px); // compensates for expand button

      &:first-child {
        margin-top: 0;
      }

      position: relative;

      .select {
        display: flex;
        overflow: hidden;

        position: relative;
        width: 100%;
        margin-bottom: 0.3em;
        text-align: left;
        user-select: none;
        // border-radius: 5px;

        background-color: transparent;
        border: 1px solid var(--light-grey);

        &:disabled {
          opacity: 0.6;
        }

        &:focus,
        &.focused {
          outline: 1px solid #ddd;
        }

        &.focused {
          // outline: 1px solid var(--text-colour);
          border: 1px solid var(--link-colour);
          font-weight: bold;
          // visibility: hidden;
          // border-bottom-left-radius: 0;
          // border-bottom-right-radius: 0;
          // border-width: 2px;
        }

        &.has-options-selected {
          color: var(--selected);
          border-color: var(--selected);

          &.focused,
          &.focused + .list {
            border-color: var(--selected) !important;
          }
        }

        .filter-name {
          white-space: nowrap;
          overflow: hidden;
          padding-right: 2px; // cuts off italic text otherwise
        }

        i.expand {
          position: absolute;
          right: 0;
          // top: 0;
          background-color: white;

          padding: 4px 6px;
          border-left: 1px solid var(--light-grey);
        }
      }

      .list {
        position: absolute;
        background-color: white;
        width: 100%; //calc(100% + 2px);
        margin-left: -1px; // 0;
        margin-bottom: 5em;

        top: 28px; //35px;
        left: 1px;
        z-index: 99;
        border: 1px solid var(--link-colour);
        // outline: 1px solid var(--link-colour);
        box-shadow: 0 3px 10px #ccc;
        border-top: none;

        .filter-search {
          border: 1px solid #aaa;
          padding: 5px;
          outline: 1px solid var(--selected);
        }

        .options {
          max-height: 300px;
          font-weight: 100;
          // transform: translateY(100%);
          overflow-y: auto;
          overscroll-behavior: none;

          // box-shadow: 1px 2px 4px #888a;
          color: var(--text-colour);
          background-color: white;

          .option {
            display: flex;
            align-items: center;
            white-space: nowrap;
            // padding: 0.3em 0.5em;
            padding: 0.1em 0.5em;
            margin: 5px 0;
            font-size: 0.95em;

            input[type='checkbox'] {
              $size: 18px;
              height: $size;
              max-width: $size;
              min-width: $size;
            }

            label {
              width: 100%;
              white-space: normal;
              word-break: break-word;
              overflow: hidden;
              margin: 0;
            }
          }
        }
      }
    }
  }
}

ul {
  list-style-type: none;
  margin: 0;
}

.active-search-terms {
  width: 100%;
  max-height: 500px; // max(500px, 30vh);
  overflow: auto;
  padding-left: 0.5em;

  li {
    padding-left: 0.5em;
    padding-right: 2em;

    &:hover {
      background-color: #eee;
    }
  }
}

.override-toggles {
  // border: 1px solid grey;
  border: none;
  border-radius: 5px;
  background-color: white;
  border-bottom: 1px solid #ddd;

  &.active {
    background-color: var(--primary);
    color: white;
  }

  &.excluded {
    background-color: var(--danger);
    color: white;
  }

  &:focus {
    outline: none;
  }

  input[type='checkbox'] {
    $size: 18px;
    height: $size;
    width: $size;
  }
}

.custom-filter {
  display: flex;
  // border-top: 1px dashed #aaa;
  padding: 0.5em 0;
  margin: 0.5em 0;
}

.no-terms {
  font-style: italic;
}
.none {
  // border-bottom: 1px solid var(--light-grey);
  font-style: italic;
}
</style>
